import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ResponseDto } from '@models/responseDto.model';
import { GroupUser, GroupsCurrentUser } from '@models/group-user.model';
import { ProfileType } from '@variables/profileType';
import { SelectSystemService } from '@modules/commom/select-profile/services/select-system.service';
import { NotifyService } from '@services/notify.service';
import { LocalstorageService } from '@services/localstorage.service';
import { AbstractConfigurationBase } from 'src/app/shared/abstracts/abstract-configuration-base';

const EMPTY_PROFILE = 0;
const UNIQUE_PROFILE = 1;

@Component({
    selector: 'app-select-profile',
    templateUrl: './select-profile.component.html',
    styleUrls: ['./select-profile.component.scss'],
    standalone: false
})
export class SelectProfileComponent extends AbstractConfigurationBase implements OnInit {

  isLoading = true;
  uniqueProfiles: number[] = [];
  isNotAutorized = false;
  isTokenEdenredConnect = true;

  private uniqueProfilesClient: number[] = [];

  readonly profileType = ProfileType;

  constructor(private readonly route: Router,
              private readonly selectSystemService: SelectSystemService,
              private readonly notifyService: NotifyService,
              private readonly localStorageService: LocalstorageService
  ) {
    super();
  }

  ngOnInit(): void {
    this.authService.refreshRoles();

    this.selectSystemService.groupsByCurrentUser$
      .subscribe({
        next: (response) => this.handleProfileGroupUserResponse(response),
        error: () => {
          this.isNotAutorized = true;
          this.isLoading = false;
          console.warn('Não foi possível carregar os perfis do usuário. Verifique se o usuário possui perfil associado.');
        }
      });
  }

  private handleProfileGroupUserResponse(responseDto: ResponseDto<GroupsCurrentUser>): void {
    if (this.isResponseEmpty(responseDto)) {
      this.notifyService.showWarning('Atenção!', 'Usuário não possui permissão para acessar o sistema. Você será redirecionado para área de login.');
      setTimeout(() => this.logOut(), 5000);
      return;
    }

    const allIdProfiles: number[] = this.extractProfileIds(responseDto);
    this.uniqueProfiles = this.getUniqueIds(allIdProfiles);

    const userId: number = responseDto.data?.userID;
    this.storageCurrentUserId(userId);
    this.mixpanelService.identify(userId?.toString());

    this.isTokenEdenredConnect = this.authService.isTokenEdenredConnect;
    this.isTokenEdenredConnect ? this.handleEdenredConnectProfiles() : this.handleBackOfficeProfiles();

    this.disablePageLoadAnimation();
  }

  private isResponseEmpty(responseDto: ResponseDto<GroupsCurrentUser>): boolean {
    return responseDto.data.listGroups.length === EMPTY_PROFILE;
  }

  private extractProfileIds(responseDto: ResponseDto<GroupsCurrentUser>): number[] {
    return responseDto.data.listGroups.map((groupUser: GroupUser) => groupUser.grupoTipoID);
  }

  private getUniqueIds(allIdProfiles: number[]): number[] {
    return Array.from(new Set(allIdProfiles));
  }

  private setHasMultipleProfiles(profiles: number[], isBackOffice: boolean): void {
    const hasMultipleProfiles = profiles.length > UNIQUE_PROFILE
      && profiles.includes(isBackOffice ? ProfileType.BACKOFFICE : ProfileType.PARTNER);

    this.userProfileService.setHasMultipleProfile(hasMultipleProfiles);
  }

  private handleSingleProfile(profiles: number[]): void {
    if (profiles.length === UNIQUE_PROFILE || !this.userProfileService.hasMultipleProfile()) {
      this.userProfileService.setUserProfile(profiles[0]);
      this.actionByProfile();
    }
  }

  private actionByProfile(): void {
    const profileAction: any = this.userProfileService.getActionForProfile();
    this.route.navigateByUrl(profileAction.url);
  }

  private logOut(): void {
    this.authService.logOut();
  }

  private disablePageLoadAnimation(): void {
    if (this.userProfileService.hasMultipleProfile()) {
      this.isLoading = false;
    }
  }

  toEnterCustomerPortal() {
    let profile = this.uniqueProfiles.includes(ProfileType.RELATIONSHIP) ? ProfileType.RELATIONSHIP : ProfileType.CUSTOMER;
    this.userProfileService.setUserProfile(profile);
    this.actionByProfile();
  }

  toEnterBackOfficePortal() {
    this.userProfileService.setUserProfile(ProfileType.BACKOFFICE);
    this.actionByProfile();
  }

  private storageCurrentUserId(userId: number) {
    this.userProfileService.setUserId(userId);
  }

  toEnterPartnerPortal() {
    this.userProfileService.setUserProfile(ProfileType.PARTNER);
    this.authService.removeContractId();
    this.localStorageService.removeContractId();
    this.actionByProfile();
  }

  private handleEdenredConnectProfiles() {
    this.uniqueProfilesClient = this.uniqueProfiles.filter(profile => profile !== ProfileType.BACKOFFICE );

    this.setHasMultipleProfiles(this.uniqueProfilesClient, false);
    this.handleSingleProfile(this.uniqueProfilesClient)
  }

  private handleBackOfficeProfiles() {
    this.setHasMultipleProfiles(this.uniqueProfiles, true);
    this.handleSingleProfile(this.uniqueProfiles);
  }
}
